import { useEffect, useRef, useState, useMemo } from 'react';

interface IProps {
  referenceTime: number; // Unix timestamp in milliseconds
  duration: string; // Duration in minutes as a string
  onTimeFinish: () => void;
}

const useTimer = ({ duration, referenceTime, onTimeFinish }: IProps) => {
  // Memoize the end time calculation to avoid recalculations on re-renders
  const endTime = useMemo(() => {
    const durationInMinutes = parseInt(duration, 10); // Parse duration as minutes
    const durationInSeconds = durationInMinutes * 60; // Convert minutes to seconds
    return referenceTime + durationInSeconds * 1000; // Calculate end time in milliseconds
  }, [referenceTime, duration]);

  // Store the end time in a ref for consistent access
  const endTimeRef = useRef<number>(endTime);

  // Initialize the remaining time
  const [timeLeft, setTimeLeft] = useState<number>(() => {
    return Math.max(0, Math.floor((endTimeRef.current - Date.now()) / 1000)); // Remaining time in seconds
  });

  useEffect(() => {
    if (timeLeft > 0) {
      const intervalId = setInterval(() => {
        const currentTime = Date.now();
        const remainingTime = Math.max(0, Math.floor((endTimeRef.current - currentTime) / 1000));
        setTimeLeft(remainingTime);

        if (remainingTime === 0) {
          clearInterval(intervalId);
          onTimeFinish();
        }
      }, 1000);
    } else {
      onTimeFinish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  return {
    timeLeft,
  };
};

export default useTimer;
